import React, { useContext, useEffect, useRef, useState } from 'react'
import { Alert, Button, CircularProgress } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import StarterCode from '../../../topBar/StarterCode'
import { SessionContext } from '../../../../context/SessionContext'
import { cppDefinesMain, testCodeForTestsSetting, testsEnabled } from '../../sessionSettings'
import { getSessionById } from '../../../../service/sessionService'
import { updateTestCodeInSession } from '../../../../service/testService'
import { draftStarterCode } from '../../../../service/gptService'
import { isClassStyle } from '../../testResults'

// Exercise types whose Student Code Template the AI can draft: a Python or C++ function.
const STARTER_LANGUAGE = { Python: 'python', Vizmental: 'python', CPP: 'cpp' }

// Sessions whose empty template was already drafted once on arrival, so coming back to this
// step after clearing the template on purpose does not fill it again.
const draftedOnArrival = new Set()

// Testcases calls this after it clears a drafted template (the exercise changed between a
// function and a class), so arriving here drafts the new kind.
export const forgetArrivalDraft = (sessionId) => draftedOnArrival.delete(sessionId)

// Final step for code and SQL types. Renders the existing StarterCode panel unmodified.
//
// Ordering note: this runs AFTER Testcases on purpose. TestList's "Create Unit Tests"
// writes the whole test_code object with `template: ''` (createTestCode returns an empty
// template at testService.js:349; TestList writes it at :128 and :180), so a student code
// template authored before that step would be silently blanked. Authoring it here, last,
// is safe.
//
// StarterCode seeds its three editors from session.test_code in an effect keyed on the
// session object, and its editors blur-save. The wizard refetches the session before
// entering this step and remounts on the fetched identity so the editors never start from
// their '#StarterCode' / '#Unit Test' placeholder defaults and blur those over real code.
//
// For Python and C++ the AI drafts the Student Code Template from the problem description:
// just the function, named as the description names it, with an empty parameter list (the
// parameters are the student's to work out). It does so on arrival when the template is
// empty, and on request. For a class exercise it drafts the class line alone.
// `onStarterSaved` re-reads the row so StarterCode re-seeds from the drafted template.
const CodeStubStep = ({ typeMeta, refreshToken, onTemplateDraftChange, onStarterSaved }) => {
  const { session } = useContext(SessionContext)
  const isSql = typeMeta?.kind === 'sql'
  const hasTests = testsEnabled(session)
  const isCpp = session?.type === 'CPP'
  const templateHasMain = cppDefinesMain(session?.test_code?.template)
  const starterLanguage = STARTER_LANGUAGE[session?.type]
  const isClass = isClassStyle(session)

  // What the template editor holds right now, saved or not.
  const typedTemplateRef = useRef(session?.test_code?.template ?? '')
  const reportTemplate = (code) => {
    typedTemplateRef.current = code ?? ''
    onTemplateDraftChange?.(code)
  }
  const [drafting, setDrafting] = useState(false)
  const [draftNote, setDraftNote] = useState(null) // { severity, text }

  // `onArrival`: only fill a template nobody has started on - not one the instructor typed
  // into while the draft was on its way.
  const draftStarter = async ({ onArrival = false } = {}) => {
    if (!session?.id || !starterLanguage || drafting) return
    setDrafting(true)
    setDraftNote(null)
    try {
      const code = await draftStarterCode({
        task: session.task,
        language: starterLanguage,
        style: isClass ? 'class' : undefined,
      })
      const fresh = await getSessionById(session.id)
      if (onArrival && (typedTemplateRef.current.trim() || fresh?.test_code?.template?.trim())) return
      const testCode = { starter: '', unit_test: '', ...(fresh?.test_code || {}), template: code }
      // C++ with test cases off needs a main() for students to write their program in.
      const next = testCodeForTestsSetting({ type: session.type, test_code: testCode }, hasTests) || testCode
      if (!(await updateTestCodeInSession(session.id, next))) {
        setDraftNote({ severity: 'error', text: 'Could not save the starter code. Check your connection and try again.' })
        return
      }
      await onStarterSaved?.()
      setDraftNote({ severity: 'success', text: 'Starter code drafted from the problem description. Edit it as you like.' })
    } catch (error) {
      console.error('Drafting the starter code failed:', error)
      setDraftNote({ severity: 'warning', text: 'Could not draft starter code right now. Write it below, or try again.' })
    } finally {
      setDrafting(false)
    }
  }

  useEffect(() => {
    if (!session?.id || !starterLanguage || draftedOnArrival.has(session.id)) return
    if (session.test_code?.template?.trim() || !session.task?.trim()) return
    draftedOnArrival.add(session.id)
    draftStarter({ onArrival: true })
  }, [session?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="cq-card__head">
        <div>
          <h2>Code stubs</h2>
          <p>
            {isSql
              ? 'Author the starting schema and generate a reference query.'
              : 'Review the generated harness and set the code students start from.'}
          </p>
        </div>
      </div>

      {!isSql && hasTests && !session?.test_code?.unit_test && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No test harness has been generated yet. Go back to <strong>Testcases</strong> and
          use <strong>Create Unit Tests</strong>, or write the harness by hand below —
          without it, students get an exercise that reports every test as not run.
        </Alert>
      )}

      {!isSql && hasTests && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Variable Setup</strong> and <strong>Unit Test</strong> were generated in
          the previous step — edit them only if you need to. <strong>Student Code
          Template</strong> is what appears in the student&apos;s editor, and it is empty
          until you fill it in here. Each editor saves when it loses focus.
        </Alert>
      )}

      {!isSql && !hasTests && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Test cases are off, so there is no grading harness. <strong>Student Code
          Template</strong> is what appears in the student&apos;s editor. It saves when it
          loses focus.
        </Alert>
      )}

      {/* C++ gets main() from the generated harness when tests are on and from the
          template when they are off. The server keeps either case compiling (it adds an
          empty main() or sets a clashing one aside), so this is about what students
          start from, not about the build. */}
      {isCpp && !hasTests && !templateHasMain && (
        <Alert severity="info" sx={{ mb: 2 }}>
          With test cases off, students run a whole program. Give the template a{' '}
          <code>main()</code> for them to write in — without one, Run compiles but prints
          nothing.
        </Alert>
      )}
      {isCpp && hasTests && templateHasMain && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The test harness provides <code>main()</code>, so the template&apos;s own{' '}
          <code>main()</code> is set aside when tests run. Consider removing it so students
          focus on the function being tested.
        </Alert>
      )}

      {isSql && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Write the schema students start from, then use <strong>Generate AI Solution</strong>{' '}
          to author the reference query their answer is diffed against.
        </Alert>
      )}

      {starterLanguage && (
        <div className="cq-starter-ai">
          <Button
            size="small"
            startIcon={drafting ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
            onClick={() => draftStarter()}
            disabled={drafting}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            {session?.test_code?.template?.trim() ? 'Redraft with AI' : 'Draft with AI'}
          </Button>
          <span className="cq-saved-hint">
            {drafting
              ? `Drafting the ${isClass ? 'class' : 'function'} from the problem description…`
              : isClass
                ? 'Starter code from the problem description: just the class line - students write the methods. Replaces the Student Code Template.'
                : 'Starter code from the problem description: just the function name, with no parameters - students add those. Replaces the Student Code Template.'}
          </span>
        </div>
      )}
      {draftNote && (
        <Alert severity={draftNote.severity} sx={{ mb: 2 }} onClose={() => setDraftNote(null)}>
          {draftNote.text}
        </Alert>
      )}

      <div className="cq-embed">
        <StarterCode
          key={`${session?.id}-${refreshToken}`}
          hideHarness={!isSql && !hasTests}
          onTemplateDraftChange={reportTemplate}
        />
      </div>
    </>
  )
}

export default CodeStubStep
